import { useState } from 'react';
import {
  User,
  BookOpen,
  Phone,
  MessageSquare,
  Mail,
  Linkedin,
  Calendar,
  ChevronDown,
  ChevronRight,
  Video,
  CreditCard,
  BarChart3,
  Wallet,
  Receipt
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { canSeeBilling } from '../../utils/permissions';

interface SettingsNavProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
}

export function SettingsNav({ activeSection, onSectionChange }: SettingsNavProps) {
  const { user } = useAuth();
  const showBilling = canSeeBilling(user);
  const [aiAgentsExpanded, setAiAgentsExpanded] = useState(true);
  const [connectionsExpanded, setConnectionsExpanded] = useState(true);
  const [billingExpanded, setBillingExpanded] = useState(true);

  const sections = [
    {
      id: 'profile',
      label: 'Profile',
      icon: User,
      group: 'general',
    },
    {
      id: 'sales-playbook',
      label: 'Sales Playbook',
      icon: BookOpen,
      group: 'general',
    },
    {
      id: 'call-agent',
      label: 'Call Agent',
      icon: Phone,
      group: 'agents',
    },
    {
      id: 'whatsapp-agent',
      label: 'WhatsApp Agent',
      icon: MessageSquare,
      group: 'agents',
    },
    {
      id: 'email-agent',
      label: 'Email Agent',
      icon: Mail,
      group: 'agents',
    },
    {
      id: 'linkedin-agent',
      label: 'LinkedIn Agent',
      icon: Linkedin,
      group: 'agents',
    },
    {
      id: 'phone-numbers',
      label: 'Phone Numbers',
      icon: Phone,
      group: 'connections',
    },
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      icon: MessageSquare,
      group: 'connections',
    },
    {
      id: 'emails',
      label: 'Emails',
      icon: Mail,
      group: 'connections',
    },
    {
      id: 'linkedin',
      label: 'LinkedIn',
      icon: Linkedin,
      group: 'connections',
    },
    {
      id: 'calendar',
      label: 'Calendars',
      icon: Calendar,
      group: 'connections',
    },
    {
      id: 'video-calls',
      label: 'Video Calls',
      icon: Video,
      group: 'connections',
    },
    {
      id: 'subscription',
      label: 'Subscription',
      icon: CreditCard,
      group: 'billing',
    },
    {
      id: 'usage-limits',
      label: 'Usage & Limits',
      icon: BarChart3,
      group: 'billing',
    },
    {
      id: 'payment-method',
      label: 'Payment Method',
      icon: Wallet,
      group: 'billing',
      hidden: true,
    },
    {
      id: 'transactions',
      label: 'Transactions',
      icon: Receipt,
      group: 'billing',
      hidden: true,
    },
  ];

  const generalSections = sections.filter(s => s.group === 'general');
  const agentSections = sections.filter(s => s.group === 'agents');
  const connectionSections = sections.filter(s => s.group === 'connections');
  const billingSections = sections.filter(s => s.group === 'billing' && !s.hidden);

  return (
    <nav className="space-y-1">
      <div className="px-3 py-2">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-widest font-sans">
          General
        </h3>
      </div>
      {generalSections.map((section) => {
        const Icon = section.icon;
        const isActive = activeSection === section.id;
        return (
          <button
            key={section.id}
            onClick={() => onSectionChange(section.id)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-300 ${
              isActive
                ? 'bg-gradient-to-r from-[#1875FF]/10 to-[#05E0D1]/10 text-[#1875FF] border-l-4 border-[#05E0D1] shadow-sm'
                : 'text-gray-700 hover:bg-[#1875FF]/5 hover:-translate-x-0.5'
            }`}
          >
            <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${
              isActive ? 'bg-gradient-to-br from-[#1875FF]/20 to-[#05E0D1]/20' : 'bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10'
            }`}>
              <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-[#1875FF]' : 'text-gray-500'}`} />
            </div>
            <span>{section.label}</span>
          </button>
        );
      })}

      <div className="pt-4">
        <button
          onClick={() => setAiAgentsExpanded(!aiAgentsExpanded)}
          className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-widest font-sans hover:text-gray-700 transition-colors"
        >
          <span>AI Agents</span>
          {aiAgentsExpanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>
        {aiAgentsExpanded && (
          <div className="mt-1 space-y-1">
            {agentSections.map((section) => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;
              return (
                <button
                  key={section.id}
                  onClick={() => onSectionChange(section.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-300 ${
                    isActive
                      ? 'bg-gradient-to-r from-[#1875FF]/10 to-[#05E0D1]/10 text-[#1875FF] border-l-4 border-[#05E0D1] shadow-sm'
                      : 'text-gray-700 hover:bg-[#1875FF]/5 hover:-translate-x-0.5'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${
                    isActive ? 'bg-gradient-to-br from-[#1875FF]/20 to-[#05E0D1]/20' : 'bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10'
                  }`}>
                    <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-[#1875FF]' : 'text-gray-500'}`} />
                  </div>
                  <span>{section.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="pt-4">
        <button
          onClick={() => setConnectionsExpanded(!connectionsExpanded)}
          className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-widest font-sans hover:text-gray-700 transition-colors"
        >
          <span>Connections</span>
          {connectionsExpanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>
        {connectionsExpanded && (
          <div className="mt-1 space-y-1">
            {connectionSections.map((section) => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;
              return (
                <button
                  key={section.id}
                  onClick={() => onSectionChange(section.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-300 ${
                    isActive
                      ? 'bg-gradient-to-r from-[#1875FF]/10 to-[#05E0D1]/10 text-[#1875FF] border-l-4 border-[#05E0D1] shadow-sm'
                      : 'text-gray-700 hover:bg-[#1875FF]/5 hover:-translate-x-0.5'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${
                    isActive ? 'bg-gradient-to-br from-[#1875FF]/20 to-[#05E0D1]/20' : 'bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10'
                  }`}>
                    <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-[#1875FF]' : 'text-gray-500'}`} />
                  </div>
                  <span>{section.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {showBilling && (
        <div className="pt-4">
          <button
            onClick={() => setBillingExpanded(!billingExpanded)}
            className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-widest font-sans hover:text-gray-700 transition-colors"
          >
            <span>Billing</span>
            {billingExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
          {billingExpanded && (
            <div className="mt-1 space-y-1">
              {billingSections.map((section) => {
                const Icon = section.icon;
                const isActive = activeSection === section.id;
                return (
                  <button
                    key={section.id}
                    onClick={() => onSectionChange(section.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-300 ${
                      isActive
                        ? 'bg-gradient-to-r from-[#1875FF]/10 to-[#05E0D1]/10 text-[#1875FF] border-l-4 border-[#05E0D1] shadow-sm'
                        : 'text-gray-700 hover:bg-[#1875FF]/5 hover:-translate-x-0.5'
                    }`}
                  >
                    <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${
                      isActive ? 'bg-gradient-to-br from-[#1875FF]/20 to-[#05E0D1]/20' : 'bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10'
                    }`}>
                      <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-[#1875FF]' : 'text-gray-500'}`} />
                    </div>
                    <span>{section.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </nav>
  );
}
